/**
 * Push an event into the GTM dataLayer.
 *
 * Deliberately independent of gtag: the two are separate transports for the
 * same event, and a blocked or absent gtag.js must not also silence GTM.
 * dataLayer is initialised in index.html before either tag loads, so a push
 * that lands before GTM is ready is replayed by the container on init.
 */
const pushToDataLayer = (event: string, params?: Record<string, unknown>): void => {
  try {
    const dataLayer = (window as any).dataLayer;
    if (!Array.isArray(dataLayer)) return;
    dataLayer.push({ event, ...(params || {}) });
  } catch {
    // never let analytics break the app
  }
};

/**
 * Event tracking helper — sends to GA4 via window.gtag AND to the GTM
 * dataLayer, so container tags (Meta CompleteRegistration, LinkedIn, ...) can
 * trigger on the same events GA4 already receives.
 *
 * Safe to call when either transport is absent (ad-blockers, consent not yet
 * given). Each is attempted independently — one failing never skips the other.
 */
export const track = (event: string, params?: Record<string, unknown>): void => {
  try {
    const gtag = (window as any).gtag;
    if (typeof gtag === "function") {
      gtag("event", event, params || {});
    }
  } catch {
    // never let analytics break the app
  }
  pushToDataLayer(event, params);
};

/**
 * SPA pageview — GA4 only auto-fires page_view on the initial document load,
 * so every client-side route change after that was invisible. Sends the
 * explicit event to the GA4 stream, and mirrors it into dataLayer so GTM
 * container tags can trigger on navigation too.
 */
export const trackPageView = (path: string, title?: string): void => {
  try {
    const gtag = (window as any).gtag;
    if (typeof gtag === "function") {
      gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: title || document.title,
      });
    }
    const dataLayer = (window as any).dataLayer;
    if (Array.isArray(dataLayer)) {
      dataLayer.push({ event: "spa_page_view", page_path: path });
    }
  } catch {
    // never let analytics break the app
  }
};
